"use client";

import { cn } from "@/lib/utils";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ShieldCheck,
  Zap,
  Database,
  Lock,
  Minus,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import WhatsAppCTA from "./WhatsAppCTA";
import { useState, useCallback } from "react";
import { useSectionViewGoal } from "@/hooks/useSectionViewGoal";
import { SectionHeading } from "./primitives";
import { buttonClass, CONTAINER, SECTION_Y } from "./ui-tokens";

// Todos os planos têm acesso aos mesmos módulos e ao mesmo suporte — o que
// muda entre eles é só o limite de pessoas ativas. Não gatear funcionalidade
// por plano (features[0] é sempre "os mais de 20 módulos liberados") é proposital:
// evita contradizer a promessa de "mais de 20 módulos, uma mensalidade" da seção de
// Features.
//
// A ORDEM das linhas de cada card é deliberada, e não é a ordem em que a gente
// pensa nos planos. O olho não lê lista de preço de cima a baixo: ele fixa no
// primeiro e no último item e atravessa o meio. Então:
//   [0] o que justifica a mensalidade inteira — os mais de 20 módulos sem nada à parte;
//   [1] o utilitário, que existe para comparar um plano com o outro;
//   [2] colado no botão, o que reduz o risco de clicar (implantação,
//       migração e suporte inclusos).
//
// Os cards maiores prometiam "suporte prioritário", "auditoria avançada de
// acessos", "suporte VIP", "gerente de sucesso dedicado e SLA garantido" — nada
// disso existe: auditoria e papéis estão em todos os planos, e o suporte é o
// mesmo para todos (29/10/2026). Não volte a diferenciar plano por promessa
// que o sistema ou a operação não cumprem.
// O teto de pessoas saiu das linhas: ele já aparece no topo do card, e repetido
// como primeiro item ele abria o cartão com uma restrição no lugar de um ganho.
//
// A `description` descreve a IGREJA, não o plano. Ela dizia "para igrejas
// pequenas dando os primeiros passos na gestão digital", "mais capacidade e
// suporte ágil para igrejas em expansão", "alta performance para igrejas em
// plena expansão" — seis variações de intensidade que não ajudam ninguém a
// escolher, ocupando altura fixa em cada card. Quem lê precisa se reconhecer
// numa frase e parar ali, então cada uma agora descreve um arranjo concreto de
// equipe. O critério numérico continua sendo o teto de pessoas, no topo do
// card e no simulador.
//
// A regra do limite abaixo (COUNTING_RULE) espelha o que o código realmente
// faz: `getChurchMembersCount` conta a coleção `members` com isActive == true
// (membros e frequentadores). A criança do Kids cadastrada como membro está
// nessa coleção e conta; visitantes (`visitors`, inclusive a criança visitante)
// não consomem cota; ministérios, grupos e eventos são -1 (ilimitados) em
// todos os planos — ver plan-limits.ts.
// Ao mexer aqui, confira lá primeiro.
export const TIERS = [
  {
    id: "starter",
    name: "Essencial",
    price: { monthly: 97 },
    description:
      "Uma congregação, uma pessoa cuidando da secretaria.",
    maxMembers: 150,
    features: [
      "Os mais de 20 módulos liberados, nenhum cobrado à parte",
      "Usuários do sistema, ministérios e células ilimitados",
      "Implantação, migração e suporte no WhatsApp",
    ],
    cta: "Testar Essencial grátis",
    highlighted: false,
  },
  {
    id: "standard",
    name: "Crescimento",
    price: { monthly: 167 },
    description:
      "Já tem célula, escala de ministério e alguém só para a tesouraria.",
    maxMembers: 350,
    features: [
      "Os mais de 20 módulos liberados, nenhum cobrado à parte",
      "Usuários do sistema, ministérios e células ilimitados",
      "Implantação, migração e suporte no WhatsApp",
    ],
    cta: "Testar Crescimento grátis",
    highlighted: true,
    popular: true,
  },
  {
    id: "growth",
    name: "Avançado",
    price: { monthly: 297 },
    description:
      "Vários ministérios escalando toda semana e eventos com inscrição.",
    maxMembers: 700,
    features: [
      "Os mais de 20 módulos liberados, nenhum cobrado à parte",
      "Usuários do sistema, ministérios e células ilimitados",
      "Implantação, migração e suporte no WhatsApp",
    ],
    cta: "Testar Avançado grátis",
    highlighted: false,
  },
  {
    id: "pro",
    name: "Premium",
    price: { monthly: 497 },
    description:
      "Departamentos com líder próprio e mais de uma pessoa no financeiro.",
    maxMembers: 1200,
    features: [
      "Os mais de 20 módulos liberados, nenhum cobrado à parte",
      "Usuários do sistema, ministérios e células ilimitados",
      "Implantação, migração e suporte no WhatsApp",
    ],
    cta: "Testar Premium grátis",
    highlighted: false,
  },
  {
    id: "elite",
    name: "Elite",
    price: { monthly: 797 },
    description:
      "Equipe administrativa própria e conselho que cobra relatório.",
    maxMembers: 2000,
    features: [
      "Os mais de 20 módulos liberados, nenhum cobrado à parte",
      "Usuários do sistema, ministérios e células ilimitados",
      "Implantação, migração e suporte no WhatsApp",
    ],
    cta: "Testar Elite grátis",
    highlighted: false,
  },
  {
    id: "ultimate",
    name: "Ultimate",
    price: { monthly: 1197 },
    description:
      "Estrutura administrativa com várias frentes e equipe em cada uma.",
    maxMembers: 3000,
    features: [
      "Os mais de 20 módulos liberados, nenhum cobrado à parte",
      "Usuários do sistema, ministérios e células ilimitados",
      "Implantação, migração e suporte no WhatsApp",
    ],
    cta: "Testar Ultimate grátis",
    highlighted: false,
  },
];

const guarantees = [
  {
    icon: ShieldCheck,
    title: "7 dias grátis para testar",
    desc: "O cartão é cadastrado no início, mas nada é cobrado durante o teste: a primeira cobrança acontece no 8º dia, e avisamos por e-mail no dia anterior. Cancele antes e não paga nada. Depois disso, ainda valem 7 dias de garantia com reembolso integral, se a igreja não tiver usado as cinco funções que liberam na primeira cobrança.",
  },
  {
    icon: Zap,
    title: "Implantação gratuita",
    desc: "Nossa equipe deixa a plataforma pronta para sua igreja usar no próximo culto.",
  },
  {
    icon: Database,
    title: "Migração em até 72h",
    desc: "Trazemos suas planilhas e cadastros antigos gratuitamente para você não perder tempo digitando.",
  },
  // Promessa permanente, não campanha: enquanto ela estiver aqui, nenhum
  // reajuste futuro alcança cliente ativo. O código que a sustenta são as
  // coortes de preço em src/lib/plan-prices.ts.
  {
    icon: Lock,
    title: "Seu preço nunca sobe",
    desc: "O valor do dia em que você assina é o valor que você paga enquanto for cliente. Quando a tabela é reajustada, ela vale para quem chega depois. E se a sua igreja crescer e mudar de plano, o plano novo também sai pela tabela antiga.",
  },
];

type Tier = (typeof TIERS)[number];

const MAX_TIER = TIERS[TIERS.length - 1];

const brl = (n: number) => n.toLocaleString("pt-BR");
const perDay = (monthly: number) =>
  (monthly / 30).toFixed(2).replace(".", ",");

/* ── Simulador de plano ────────────────────────────────────────────── */
// A dúvida "1.500 membros + 300 frequentadores = qual plano?" derruba
// conversão de igreja grande. Aqui a regra fica explícita e o visitante
// descobre o plano dele sem abrir a FAQ nem chamar no WhatsApp.
function PlanFinder() {
  const { trackCustomGoal } = useDataFastGoals();
  const [value, setValue] = useState("");

  const people = parseInt(value.replace(/\D/g, ""), 10);
  const hasAnswer = Number.isFinite(people) && people > 0;
  const overMax = hasAnswer && people > MAX_TIER.maxMembers;
  const match = hasAnswer
    ? TIERS.find((tier) => people <= tier.maxMembers)
    : undefined;

  return (
    <div className="lp-reveal lp-surface grid overflow-hidden md:grid-cols-2">
      {/* Regra de contagem */}
      <div className="p-6 md:p-8">
        <h3 className="text-lg font-semibold tracking-tight text-foreground">
          Como o limite do plano é contado
        </h3>
        <ul className="mt-4 space-y-3 text-sm leading-relaxed">
          <li className="flex gap-2.5">
            <Check
              className="mt-0.5 size-4 shrink-0 text-primary"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-foreground">
              <strong className="font-semibold">Contam:</strong> membros e
              frequentadores com cadastro ativo.
            </span>
          </li>
          <li className="flex gap-2.5">
            <Minus
              className="mt-0.5 size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="text-muted-foreground">
              <strong className="font-semibold text-foreground">
                Não contam:
              </strong>{" "}
              visitantes (a criança visitante do Kids também) e cadastros
              inativos.
            </span>
          </li>
          <li className="flex gap-2.5">
            <Check
              className="mt-0.5 size-4 shrink-0 text-primary"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-foreground">
              Ministérios, células e eventos são{" "}
              <strong className="font-semibold">ilimitados</strong> em todos os
              planos.
            </span>
          </li>
        </ul>
      </div>

      {/* Simulador */}
      <div className="flex flex-col border-t border-border bg-secondary/40 p-6 md:border-t-0 md:border-l md:p-8">
        <label
          htmlFor="plan-finder-people"
          className="text-sm font-semibold text-foreground"
        >
          Quantas pessoas ativas a sua igreja tem hoje?
        </label>
        {/* 16px no celular: abaixo disso o iOS dá zoom na página inteira ao
            focar o campo. */}
        <input
          id="plan-finder-people"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Ex.: 1800"
          aria-describedby="plan-finder-result"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
          onBlur={() => {
            if (hasAnswer) {
              trackCustomGoal("pricing_plan_finder_used", {
                people: String(people),
                plan: overMax ? "custom" : (match?.id ?? "none"),
              });
            }
          }}
          className="mt-2 h-12 w-full rounded-xl bg-card px-4 text-base text-foreground tabular-nums shadow-xs ring-1 ring-border transition-shadow outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring md:text-sm"
        />

        {/* Região estável e sempre presente: o leitor de tela anuncia a
            troca do texto, e o resultado não desloca o que vem abaixo. */}
        <div
          id="plan-finder-result"
          role="status"
          className="mt-4 flex min-h-20 flex-1 items-center"
        >
          {!hasAnswer ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              Some membros e frequentadores ativos. Se passar do limite depois,
              o upgrade é um clique, sem perder dado nem parar o sistema.
            </p>
          ) : overMax ? (
            <div className="space-y-3">
              <p className="text-sm leading-relaxed text-foreground">
                Acima de {brl(MAX_TIER.maxMembers)} pessoas ativas nós montamos
                um plano sob medida, com os mesmos 7 dias grátis.{" "}
                <span className="text-muted-foreground">
                  Fale com a gente no WhatsApp e trazemos a proposta.
                </span>
              </p>
              <WhatsAppCTA
                variant="outline"
                size="sm"
                text="Falar no WhatsApp"
                customMessage={`Olá! Nossa igreja tem ${brl(people)} pessoas ativas e gostaria de uma proposta do plano sob medida do Ecclesiato.`}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Seu plano é o
              <span className="mt-1 block text-2xl font-semibold tracking-tight text-foreground">
                {match?.name}{" "}
                <span className="text-muted-foreground tabular-nums">
                  · R$ {match?.price.monthly}/mês
                </span>
              </span>
              <span className="mt-1 block">
                Cobre até {brl(match?.maxMembers ?? 0)} pessoas ativas, com os
                mais de 20 módulos liberados.
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Cartão de plano ───────────────────────────────────────────────── */
// O destacado não é mais um cartão todo azul: um bloco cheio de cor ao lado
// de dois brancos lia como outro produto. Agora ele se separa por um anel e
// pelo selo de texto, e é o único cartão com o botão cheio — uma ação
// principal por vista.
function PlanCard({
  tier,
  onSubscribe,
}: {
  tier: Tier;
  onSubscribe: (tier: Tier) => void;
}) {
  const highlighted = tier.highlighted;

  return (
    <li
      className={cn(
        "lp-reveal lp-surface relative flex flex-col p-6 md:p-7",
        highlighted && "order-first shadow-md ring-2 ring-primary md:order-0",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold tracking-tight text-foreground">
          {tier.name}
        </h3>
        {highlighted ? (
          <span className="rounded-full bg-primary/8 px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-primary">
            Mais escolhido
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Até {brl(tier.maxMembers)} pessoas ativas
      </p>
      <p className="mt-4 min-h-10 text-sm leading-relaxed text-pretty text-foreground">
        {tier.description}
      </p>

      <div className="mt-6 border-t border-border pt-6">
        <p className="flex items-baseline gap-1 text-foreground">
          <span className="text-sm font-medium text-muted-foreground">R$</span>
          <span className="text-5xl leading-none font-semibold tracking-tight tabular-nums">
            {tier.price.monthly}
          </span>
          <span className="text-sm text-muted-foreground">/mês</span>
        </p>
        {/* A linha que encosta no número é a única chance de o teste ser lido
            junto com o preço — quem só percorre os cards não chega ao rodapé
            do botão. Diz QUANDO o preço começa, não um segundo jeito de dizer
            o mesmo preço. */}
        <p className="mt-2 text-sm text-muted-foreground">
          Grátis por 7 dias · depois ≈ R$ {perDay(tier.price.monthly)} por dia
        </p>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {tier.features.map((feature) => (
          <li key={feature} className="flex gap-2.5 text-sm leading-snug">
            <Check
              className="mt-0.5 size-4 shrink-0 text-primary"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => onSubscribe(tier)}
        className={buttonClass(
          highlighted ? "primary" : "secondary",
          "lg",
          "mt-8 w-full",
        )}
      >
        {tier.cta}
      </button>
      <p className="mt-2.5 text-center text-xs text-muted-foreground">
        Cobrança só no 8º dia · sem fidelidade
      </p>
    </li>
  );
}

export default function Pricing({
  sectionGoal = "pricing",
  propostaGoal = "pricing_to_proposta",
}: {
  /** Nomes dos eventos: a página /pricing usa esta seção e mede à parte. */
  sectionGoal?: string;
  propostaGoal?: string;
} = {}) {
  const { trackPricingPlanClicked, trackCustomGoal, trackNavClicked } =
    useDataFastGoals();
  const navigate = useNavigate();
  const [showOtherPlans, setShowOtherPlans] = useState(false);
  const ref = useSectionViewGoal(sectionGoal);

  const handleSubscribe = useCallback(
    (tier: Tier) => {
      trackPricingPlanClicked(tier.id, tier.price.monthly, "monthly");
      navigate(`/register?plan=${tier.id}&period=monthly`);
    },
    [trackPricingPlanClicked, navigate],
  );

  const toggleOtherPlans = () => {
    trackCustomGoal("pricing_more_plans_toggled", {
      state: showOtherPlans ? "closed" : "opened",
    });
    setShowOtherPlans((v) => !v);
  };

  const mainPlans = TIERS.slice(0, 3);
  const otherPlans = TIERS.slice(3);

  /* `content-visibility: auto` saiu daqui e da FAQ na refação: a altura
     estimada tinha sido medida para o layout antigo, e estimativa errada numa
     seção que é alvo de âncora (#precos) — ou que fica acima de outro alvo
     (#faq) — faz a rolagem suave parar fora do lugar. Se voltar, meça as duas
     alturas no navegador antes. */
  return (
    <section
      ref={ref}
      id="precos"
      aria-labelledby="precos-titulo"
      className={SECTION_Y}
    >
      <div className={CONTAINER}>
        {/* O título já disse "Menos de R$ 3,30 por dia" — que é o Essencial —
            e fixava a âncora no plano mais barato, fazendo o do meio (o alvo)
            parecer caro logo abaixo. A âncora é o que a mensalidade cobre; o
            "por dia" mora dentro de cada card, sobre o preço daquele plano. */}
        <SectionHeading
          id="precos-titulo"
          title="Uma mensalidade cobre a igreja inteira."
          muted="Os mais de 20 módulos, a implantação e a migração."
        >
          Os mais de 20 módulos vêm liberados em todos os planos, desde o Essencial.
          Sem taxa de adesão, sem fidelidade: você escolhe pelo tamanho da
          igreja, não pelo que pode usar.
        </SectionHeading>

        {/* Preço antes do simulador: a barra fixa do mobile promete "A partir
            de R$ 97/mês" e traz a pessoa para cá, e a dúvida "qual é o meu
            plano?" só nasce depois de ver que as faixas são por pessoas. */}
        <ul className="mt-12 grid items-stretch gap-5 md:grid-cols-3">
          {mainPlans.map((tier) => (
            <PlanCard key={tier.id} tier={tier} onSubscribe={handleSubscribe} />
          ))}
        </ul>

        {/* ── Planos maiores ────────────────────────────────────────── */}
        <div className="mt-5">
          <button
            type="button"
            onClick={toggleOtherPlans}
            aria-expanded={showOtherPlans}
            aria-controls="outros-planos"
            className="lp-surface flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <span>
              <span className="block text-sm font-semibold text-foreground">
                {/* Derivado do teto do último plano visível: escrito à mão,
                    dizia 500 quando o Avançado já ia até 700. */}
                Sua igreja tem mais de{" "}
                {brl(mainPlans[mainPlans.length - 1].maxMembers)} pessoas
                ativas?
              </span>
              <span className="block text-sm text-muted-foreground">
                Premium, Elite e Ultimate, até {brl(MAX_TIER.maxMembers)}{" "}
                pessoas. Acima disso, sob medida.
              </span>
            </span>
            <ChevronDown
              className={cn(
                "size-5 shrink-0 text-muted-foreground transition-[rotate] duration-200",
                showOtherPlans && "rotate-180",
              )}
              aria-hidden="true"
            />
          </button>

          {showOtherPlans && (
            <ul
              id="outros-planos"
              className="lp-open mt-3 grid gap-3 md:grid-cols-3"
            >
              {otherPlans.map((tier) => (
                <li key={tier.id} className="lp-surface flex flex-col p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold text-foreground">
                      {tier.name}
                    </h3>
                    <span className="text-sm font-semibold text-foreground tabular-nums">
                      R$ {tier.price.monthly}/mês
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Até {brl(tier.maxMembers)} pessoas
                  </p>
                  <ul className="mt-4 mb-6 flex-1 space-y-2.5">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex gap-2.5 text-sm">
                        <Check
                          className="mt-0.5 size-4 shrink-0 text-primary"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleSubscribe(tier)}
                    className={buttonClass("secondary", "md", "w-full")}
                  >
                    {tier.cta}
                  </button>
                  {/* Os três primeiros cards traziam esta linha e estes não:
                      quem abria o acordeão via só o preço cheio. */}
                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    7 dias grátis · cobrança só no 8º dia
                  </p>
                </li>
              ))}
              {/* Sem preço de propósito: a cota e o valor são combinados com
                  cada igreja (plano `custom`, ver lib/plan-limits.ts). */}
              <li className="lp-surface flex flex-col gap-4 p-6 md:col-span-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Mais de {brl(MAX_TIER.maxMembers)} pessoas ativas
                  </p>
                  <h3 className="font-semibold text-foreground">Sob medida</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Os mesmos módulos e os mesmos 7 dias grátis, com a cota e
                    o preço combinados para o tamanho da sua igreja.
                  </p>
                </div>
                <WhatsAppCTA
                  variant="outline"
                  text="Falar no WhatsApp"
                  className="md:shrink-0"
                  customMessage={`Olá! Nossa igreja tem mais de ${brl(MAX_TIER.maxMembers)} pessoas ativas e gostaria de uma proposta do plano sob medida do Ecclesiato.`}
                />
              </li>
            </ul>
          )}
        </div>

        {/* ── Regra do limite + simulador ─────────────────────────── */}
        <div className="mt-12">
          <PlanFinder />
        </div>

        {/* ── Risco ─────────────────────────────────────────────────── */}
        <ul className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {guarantees.map(({ icon: Icon, title, desc }) => (
            <li key={title} className="lp-reveal flex gap-4">
              <Icon
                className="mt-0.5 size-5 shrink-0 text-primary"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              <div>
                <p className="font-semibold text-foreground">{title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {desc}
                </p>
              </div>
            </li>
          ))}
        </ul>

        {/* Duas saídas, porque são duas pessoas diferentes: "não sei qual
            plano" e "preciso levar para o conselho". A segunda é a que trava
            a venda — em igreja quase ninguém assina sozinho — e /proposta é a
            peça que o pastor leva: preço, garantias, LGPD e portabilidade numa
            folha que se imprime. */}
        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          <div className="lp-surface flex flex-col items-start gap-4 p-6">
            <p className="font-semibold text-foreground">
              Ainda em dúvida sobre qual plano é o seu?
            </p>
            <WhatsAppCTA
              variant="outline"
              text="Falar com um consultor"
              customMessage="Olá! Gostaria de uma ajuda para escolher o plano ideal do Ecclesiato para a minha igreja."
            />
          </div>

          <div className="lp-surface flex flex-col items-start gap-4 p-6">
            <div>
              <p className="font-semibold text-foreground">
                Vai levar para a reunião de diretoria?
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Uma página com preço, garantias e política de dados, pronta
                para imprimir.
              </p>
            </div>
            <Link
              to="/proposta"
              onClick={() => trackNavClicked(propostaGoal)}
              className={buttonClass("secondary")}
            >
              Abrir a proposta para o conselho
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
